<div v-if="template_form" class="d-grid gap-4 report-builder-shell formal-wizard-shell">
  <div class="card border rounded-lg shadow-sm bg-white report-builder-header-card">
    <div class="d-flex flex-column flex-xl-row justify-content-between align-items-xl-start gap-3 mb-4">
      <div>
        <p class="text-uppercase text-muted text-xxs mb-1">Report Builder</p>
        <h3 class="text-x fw-bold mb-1">
          {{ is_editing ? "Edit PDF report template" : "Build PDF report template" }}
        </h3>
        <p class="text-muted text-xxs mb-0">
          Use the standard builder flow, then add PDF branding and sections.
        </p>
      </div>
      <span class="badge bg-light text-dark text-xxs rounded-small shadow-sm px-3 py-2">
        PDF Report
      </span>
    </div>

    <div v-if="is_editing" class="card border rounded-lg p-3 shadow-sm bg-light mb-4">
      <p class="fw-bold text-dark text-xxs mb-1">Editing saved template</p>
      <p class="text-muted text-xxs mb-0">
        This builder is preloaded from a saved template. Save when you are happy with the updated setup.
      </p>
    </div>

    <div class="card border rounded-lg p-3 shadow-sm bg-light report-builder-progress-card">
      <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <div>
          <p class="text-uppercase text-muted text-xxs mb-1">
            Step {{ current_step_index + 1 }} of {{ wizard_steps.length }}
          </p>
          <h4 class="text-x fw-bold mb-1">{{ current_step.title }}</h4>
          <p class="text-muted text-xxs mb-0">
            Move through one step at a time for a simpler setup experience.
          </p>
        </div>
      </div>

      <div class="d-flex gap-2 overflow-auto report-builder-steps mt-3">
        <button
          v-for="(step, index) in wizard_steps"
          :key="step.key"
          class="btn text-start shadow-sm report-step-pill report-step-pill-compact"
          :class="{
            'report-step-pill-active': get_step_status(step.key) === 'active',
            'report-step-pill-complete': get_step_status(step.key) === 'complete',
            'report-step-pill-upcoming': get_step_status(step.key) === 'upcoming',
          }"
          :disabled="index > current_step_index"
          type="button"
          @click="open_step(step.key)"
        >
          <div class="d-flex align-items-center gap-2">
            <span class="report-step-index-badge">{{ index + 1 }}</span>
            <div>
              <p class="fw-bold text-dark text-xxs mb-0">{{ step.title }}</p>
              <p class="text-muted text-xxs mb-0">{{ step.summary }}</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  </div>

  <div class="d-grid gap-4" :class="{ 'formal-wizard-main-grid': show_preview_panel }">
    <div class="card border rounded-lg shadow-sm bg-white report-step-card report-wizard-step-card">
      <div class="px-4 pt-4 pb-2">
        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
          <div>
            <p class="text-uppercase text-muted text-xxs mb-1">
              Step {{ current_step_index + 1 }} of {{ wizard_steps.length }}
            </p>
            <h4 class="text-x fw-bold mb-1">{{ current_step.title }}</h4>
            <p class="text-muted text-xxs mb-0">{{ current_step.summary }}</p>
          </div>
          <span class="badge rounded-small text-xxs px-3 py-2 shadow-sm bg-light text-dark">
            {{
              current_step_key === "review"
                ? "Final step"
                : "Next up"
            }}
          </span>
        </div>
      </div>
      <div class="px-4 pb-4">
        <transition name="fade-slide" mode="out-in">
          <div :key="current_step_key" class="d-grid gap-4">

        <div v-if="current_step_key === 'basics'" class="d-grid gap-4">
          <div
            v-if="can_toggle_template_save"
            class="card border rounded-lg p-3 shadow-sm bg-light"
          >
            <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <div>
                <p class="fw-bold text-dark text-xxs mb-1">Save as reusable template</p>
                <p class="text-muted text-xxs mb-0">
                  Keep this off for fast one-off runs. Turn it on only when the setup should appear in saved templates.
                </p>
              </div>
              <div class="form-check form-switch mb-0 d-flex align-items-center gap-2">
                <input
                  v-model="template_form.save_as_template"
                  class="form-check-input shadow-sm"
                  type="checkbox"
                />
                <label class="form-check-label text-xxs fw-semibold">
                  {{ template_form.save_as_template ? "Template will be saved" : "One-off PDF" }}
                </label>
              </div>
            </div>
          </div>

          <div class="row g-4">
            <div class="col-12 col-xl-4">
              <label class="modal-form-label form-label text-xxs">Report type</label>
              <input
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                type="text"
                value="PDF Report"
                disabled
              />
            </div>
            <div class="col-12 col-xl-4">
              <label class="modal-form-label form-label text-xxs">Output format</label>
              <select
                :value="selected_output_format_id"
                class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
                @change="handle_output_format_change($event.target.value)"
              >
                <option disabled value="">Select output format</option>
                <option
                  v-for="format in output_format_options"
                  :key="format.id"
                  :value="format.id"
                >
                  {{ format.display_name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-xl-4">
              <label class="modal-form-label form-label text-xxs">Template name</label>
              <input
                v-model="template_form.name"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                :disabled="template_form.save_as_template === false"
                placeholder="PDF Report"
                type="text"
              />
              <p
                v-if="template_form.save_as_template === false"
                class="text-muted text-xxs mt-2 mb-0"
              >
                A temporary file name is generated automatically for one-off PDFs.
              </p>
            </div>
            <div class="col-12">
              <label class="modal-form-label form-label text-xxs">Description</label>
              <input
                v-model="template_form.description"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                placeholder="Optional description"
                type="text"
              />
            </div>
          </div>

          <div class="row g-3">
            <div v-if="template_form.save_as_template !== false" class="col-12 col-lg-6">
              <div class="card border rounded-lg shadow-sm p-3 bg-light">
                <div class="form-check form-switch mb-0 d-flex align-items-center gap-2">
                  <input
                    v-model="template_form.is_active"
                    class="form-check-input shadow-sm"
                    type="checkbox"
                  />
                  <label class="form-check-label text-xxs fw-semibold">
                    Active template
                  </label>
                </div>
                <p class="text-muted text-xxs mb-0 mt-2">
                  Active templates are available immediately for manual or automatic generation.
                </p>
              </div>
            </div>
            <div class="col-12" :class="template_form.save_as_template !== false ? 'col-lg-6' : ''">
              <div class="card border rounded-lg shadow-sm p-3 bg-light">
                <div class="form-check form-switch mb-0 d-flex align-items-center gap-2">
                  <input
                    v-model="template_form.auto_add_to_vault"
                    class="form-check-input shadow-sm"
                    type="checkbox"
                  />
                  <label class="form-check-label text-xxs fw-semibold">
                    Add generated PDFs to the entity vault
                  </label>
                </div>
                <p class="text-muted text-xxs mb-0 mt-2">
                  Keep this on when the generated PDF should appear in the profile document vault.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="current_step_key === 'scope'" class="d-grid gap-4">
          <div
            v-if="is_profile_scope"
            class="card border rounded-lg p-3 shadow-sm bg-light"
          >
            <p class="fw-bold text-dark text-xxs mb-1">
              {{ initial_entity_name || "Selected entity" }}
            </p>
            <p class="text-muted text-xxs mb-0">
              The current profile entity type stays selected. Add more when this PDF should also be reusable for them.
            </p>
          </div>

          <formal-document-entity-type-selector
            :entity_types="entity_type_options"
            :locked_entity_type_id="target_entity_type_id"
            :selected_entity_type_ids="template_form.entity_type_ids"
            @toggle="toggle_entity_type"
          ></formal-document-entity-type-selector>

          <div class="row g-3">
            <div class="col-12 col-xl-6">
              <label class="modal-form-label form-label text-xxs">Generation mode</label>
              <select
                v-model="template_form.generation_mode"
                class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
                :disabled="template_form.save_as_template === false"
                @change="handle_generation_mode_change"
              >
                <option
                  v-for="generation_mode in generation_mode_options"
                  :key="generation_mode.internal_name"
                  :value="generation_mode.internal_name"
                >
                  {{ generation_mode.display_name }}
                </option>
              </select>
              <p
                v-if="template_form.save_as_template === false"
                class="text-muted text-xxs mt-2 mb-0"
              >
                One-off PDFs always generate manually for this profile.
              </p>
            </div>
            <div
              v-if="template_form.save_as_template !== false && template_form.generation_mode === 'on_status_transition'"
              class="col-12 col-xl-6"
            >
              <label class="modal-form-label form-label text-xxs">Trigger status</label>
              <select
                v-model="template_form.trigger_status_id"
                class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
              >
                <option disabled value="">Choose a status</option>
                <option
                  v-for="status_option in trigger_status_options"
                  :key="status_option.id"
                  :value="status_option.id"
                >
                  {{ status_option.display_name }}
                </option>
              </select>
            </div>
          </div>

        </div>

        <div v-else-if="current_step_key === 'branding'" class="d-grid gap-4">
          <div class="row g-3">
            <div class="col-12 col-xl-6">
              <label class="modal-form-label form-label text-xxs">Heading</label>
              <input
                v-model="template_form.header_title"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                placeholder="Main heading shown beside the logo"
                type="text"
              />
            </div>
            <div class="col-12 col-xl-6">
              <label class="modal-form-label form-label text-xxs">Subtitle</label>
              <input
                v-model="template_form.header_subtitle"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                placeholder="Short supporting subtitle"
                type="text"
              />
            </div>
            <div class="col-12">
              <label class="modal-form-label form-label text-xxs">Small page header</label>
              <input
                v-model="template_form.header_text"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                placeholder="Small text shown in the true page header area"
                type="text"
              />
            </div>
            <div class="col-12">
              <label class="modal-form-label form-label text-xxs">Footer text</label>
              <input
                v-model="template_form.footer_text"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                placeholder="Footer copy shown at the bottom of each page"
                type="text"
              />
            </div>
          </div>

          <div class="row g-3">
            <div class="col-12 col-xl-5">
              <label class="modal-form-label form-label text-xxs">Logo upload</label>
              <label
                class="card border rounded-small shadow-sm p-3 formal-logo-upload-label w-100"
                :class="{ 'border-success': logo_preview_url || template_form.logo_url, 'formal-logo-upload-active': is_dragging_logo }"
                @dragenter.prevent="handle_logo_drag_state(true)"
                @dragover.prevent="handle_logo_drag_state(true)"
                @dragleave.prevent="handle_logo_drag_state(false)"
                @drop.prevent="handle_logo_drop"
              >
                <input class="d-none" type="file" accept="image/*" @change="handle_logo_selection" />
                <span class="fw-semibold text-dark text-xxs">
                  {{ uploading_logo ? "Uploading..." : "Click or drag a logo here" }}
                </span>
                <span class="text-muted text-xxs mt-2">
                  PNG, JPG, or SVG.
                </span>
                <span
                  v-if="logo_preview_url || template_form.logo_url"
                  class="badge bg-success text-white text-xxs rounded-small mt-3 align-self-start"
                >
                  Uploaded
                </span>
              </label>
            </div>
            <div class="col-12 col-xl-7">
              <label class="modal-form-label form-label text-xxs">Logo URL</label>
              <input
                v-model="template_form.logo_url"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                placeholder="Upload a logo or paste an existing URL"
                type="text"
              />
              <div class="d-flex align-items-center gap-3 mt-3">
                <div class="formal-logo-preview d-flex align-items-center justify-content-center">
                  <img
                    v-if="logo_preview_url || template_form.logo_url"
                    :src="logo_preview_url || template_form.logo_url"
                    alt="Logo preview"
                  />
                </div>
                <button
                  v-if="logo_preview_url || template_form.logo_url"
                  class="btn btn-outline-danger btn-sm rounded-small text-xxs shadow-sm"
                  type="button"
                  @click="clear_logo"
                >
                  Clear logo
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="current_step_key === 'content'" class="d-grid gap-4">
          <div class="card border rounded-lg p-3 shadow-sm bg-light d-grid gap-3">
            <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <div>
                <p class="fw-bold text-dark text-xxs mb-1">Sections</p>
                <p class="text-muted text-xxs mb-0">
                  Pick a section to edit.
                </p>
              </div>
              <span class="badge bg-white text-dark text-xxs rounded-small shadow-sm px-3 py-2">
                {{ template_form.sections.length }} sections
              </span>
            </div>

            <div class="d-flex gap-2 overflow-auto report-builder-steps formal-sections-strip align-items-stretch">
              <button
                v-for="section in template_form.sections"
                :key="section.local_id"
                class="btn text-start shadow-sm report-step-pill report-step-pill-compact"
                :class="{
                  'report-step-pill-active': active_section_local_id === section.local_id,
                  'report-step-pill-complete': active_section_local_id !== section.local_id,
                }"
                type="button"
                @click="active_section_local_id = section.local_id"
              >
                <p class="fw-bold text-dark text-xxs mb-0">{{ section.title || "Untitled section" }}</p>
                <p class="text-muted text-xxs mb-0">{{ format_section_type(section.section_type) }}</p>
              </button>
            </div>

            <div class="d-flex align-items-center justify-content-between gap-2 flex-wrap">
              <p class="text-muted text-xxs mb-0 formal-sections-scroll-note">
                <i class="fas fa-arrows-alt-h me-1"></i>Scroll sideways to see and switch between all sections.
              </p>
              <span
                v-if="template_form.sections.length > 3"
                class="badge bg-white text-dark text-xxs rounded-small shadow-sm px-3 py-2"
              >
                More sections off-screen
              </span>
            </div>
          </div>

          <div class="card border rounded-lg p-3 shadow-sm bg-light">
            <div class="row g-3 align-items-end">
              <div class="col-12 col-xl-6">
                <label class="modal-form-label form-label text-xxs">New section type</label>
                <select
                  v-model="new_section_type"
                  class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
                >
                  <option
                    v-for="section_type in section_type_options"
                    :key="section_type.internal_name"
                    :value="section_type.internal_name"
                  >
                    {{ section_type.display_name }}
                  </option>
                </select>
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <button
                  class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark w-100"
                  type="button"
                  @click="add_section(1)"
                >
                  <i class="fas fa-plus me-2"></i>Add Section
                </button>
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <button
                  class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark w-100"
                  type="button"
                  @click="open_reorder_sections_modal"
                >
                  <i class="fas fa-grip-vertical me-2"></i>Reorder Sections
                </button>
              </div>
            </div>
          </div>

          <transition name="fade-slide" mode="out-in">
            <formal-document-template-section-editor
              v-if="active_section"
              :key="`${active_section.local_id}_${active_section.section_type}`"
              :checklist_field_options="checklist_field_options"
              :question_options="question_options"
              :section="active_section"
              :section_index="template_form.sections.findIndex((section) => section.local_id === active_section.local_id)"
              :section_type_options="section_type_options"
              :signatory_type_options="signatory_type_options"
              :system_field_options="system_field_options"
              :total_sections="template_form.sections.length"
              @open-question-picker="open_question_picker"
              @remove="remove_section"
            ></formal-document-template-section-editor>
          </transition>
        </div>

        <div v-else class="d-grid gap-4">
          <div class="row g-3">
            <div class="col-12 col-xl-6">
              <div class="card border rounded-small shadow-sm p-3 h-100">
                <p class="text-uppercase text-muted text-xxs mb-1">Template</p>
                <h5 class="text-x fw-bold mb-1">{{ template_form.name || "Untitled template" }}</h5>
                <p class="text-muted text-xxs mb-0">
                  {{ template_form.description || "No description added yet." }}
                </p>
              </div>
            </div>
            <div class="col-12 col-xl-6">
              <div class="card border rounded-small shadow-sm p-3 h-100">
                <p class="text-uppercase text-muted text-xxs mb-1">Applies to</p>
                <h5 class="text-x fw-bold mb-1">
                  {{ selected_entity_type_names.length ? selected_entity_type_names.join(", ") : "No entity types selected" }}
                </h5>
                <p class="text-muted text-xxs mb-0">
                  {{ review_generation_label }} generation
                </p>
              </div>
            </div>
          </div>

          <div class="card border rounded-small shadow-sm p-3">
            <p class="text-uppercase text-muted text-xxs mb-1">Branding</p>
            <div class="row g-3">
              <div class="col-12 col-xl-6">
                <div class="text-xxs fw-semibold">Heading</div>
                <div class="text-muted text-xxs">{{ template_form.header_title || "-" }}</div>
              </div>
              <div class="col-12 col-xl-6">
                <div class="text-xxs fw-semibold">Subtitle</div>
                <div class="text-muted text-xxs">{{ template_form.header_subtitle || "-" }}</div>
              </div>
              <div class="col-12 col-xl-6">
                <div class="text-xxs fw-semibold">Page header</div>
                <div class="text-muted text-xxs">{{ template_form.header_text || "-" }}</div>
              </div>
              <div class="col-12 col-xl-6">
                <div class="text-xxs fw-semibold">Footer</div>
                <div class="text-muted text-xxs">{{ template_form.footer_text || "-" }}</div>
              </div>
            </div>
          </div>

          <div class="card border rounded-small shadow-sm p-3">
            <p class="text-uppercase text-muted text-xxs mb-1">Sections</p>
            <div class="d-grid gap-2">
              <div
                v-for="(section, index) in template_form.sections"
                :key="section.local_id"
                class="d-flex justify-content-between align-items-center gap-3 border-bottom pb-2"
              >
                <div>
                  <div class="fw-semibold text-xxs">
                    {{ index + 1 }}. {{ section.title || "Untitled section" }}
                  </div>
                  <div class="text-muted text-xxs">
                    {{ format_section_type(section.section_type) }}
                  </div>
                </div>
                <span class="badge rounded-small bg-light text-dark text-xxs px-3 py-2">
                  {{ section.is_visible ? "Visible" : "Hidden" }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="generated_document" class="card border rounded-lg p-3 shadow-sm bg-light">
            <p class="fw-bold text-dark text-xxs mb-1">{{ generated_document.name }}</p>
            <p class="text-muted text-xxs mb-2">
              Status: {{ get_generated_document_status_label() }}
            </p>
            <div class="d-flex flex-column flex-sm-row gap-2">
              <button
                v-if="generated_document_url"
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                type="button"
                @click="open_generated_document"
              >
                Open PDF
              </button>
              <button
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                type="button"
                @click="go_to_generated_reports"
              >
                View Generated Reports
              </button>
            </div>
            <p v-if="generated_document.error_message" class="text-danger text-xxs mb-0 mt-2">
              {{ generated_document.error_message }}
            </p>
          </div>
        </div>
          </div>
        </transition>
      </div>
    </div>

    <transition name="preview-fade" mode="out-in">
      <formal-document-template-preview
        v-if="show_preview_panel"
        :key="`preview_${current_step_key}`"
        :preview_error="preview_error"
        :preview_html="preview_html"
        :preview_loading="preview_loading"
        :template_form="template_form"
      ></formal-document-template-preview>
    </transition>
  </div>

  <div class="card border rounded-lg shadow-sm bg-white report-builder-footer">
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 p-3">
      <div>
        <p class="text-uppercase text-muted text-xxs mb-1">PDF Template</p>
        <p class="fw-bold text-dark text-xxs mb-1">{{ template_form.name || "Untitled template" }}</p>
        <p class="text-muted text-xxs mb-0">
          {{ current_step.summary }}
        </p>
      </div>
      <div class="d-flex gap-2 justify-content-end">
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          type="button"
          @click="$emit('cancel')"
        >
          {{ cancel_label }}
        </button>
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          :disabled="current_step_index === 0"
          type="button"
          @click="go_to_previous_step"
        >
          Back
        </button>
        <button
          v-if="current_step_key !== 'review'"
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          type="button"
          @click="go_to_next_step"
        >
          Next
        </button>
        <button
          v-else
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          :disabled="saving_template"
          type="button"
          @click="save_template"
        >
          {{ saving_template ? "Saving..." : primary_submit_label }}
        </button>
      </div>
    </div>
  </div>

  <div v-if="question_picker_state.visible">
    <formal-document-question-selector
      :available_question_options="question_options"
      :entity_type_ids="template_form.entity_type_ids"
      :initial_selected_question_ids="question_picker_state.selected_question_ids"
      :modal_title="question_picker_state.modal_title"
      :project_id="project_id"
      :selection_mode="question_picker_state.selection_mode"
      @close="close_question_picker"
      @confirm="apply_question_picker_selection"
    ></formal-document-question-selector>
  </div>

  <PDFReportSignatureModal
    v-if="signature_modal_visible"
    :entity_name="initial_entity_name"
    @close="close_signature_modal"
    @confirm="confirm_signature"
  />

  <teleport to="body">
    <div
      v-if="reorder_sections_modal_visible"
      class="formal-question-selector-backdrop"
      @click.self="close_reorder_sections_modal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content bg-white rounded-3 shadow-lg">
          <div class="modal-header border-bottom-0 cute-header shadow-lg">
            <p class="modal-title text-white text-xxs">Reorder Sections</p>
            <button
              type="button"
              class="modal-btn-close btn-close"
              aria-label="Close"
              @click="close_reorder_sections_modal"
            ></button>
          </div>

          <div class="modal-body overflow-auto">
            <div class="reorder-instructions text-muted text-xxs mb-3 p-2 bg-info-subtle border-start border-3 rounded">
              <i class="fas fa-info-circle me-1"></i> Drag sections up or down to reorder them
            </div>

            <div class="sortable-list">
              <div
                v-for="(section, index) in current_reorder_sections"
                :key="section.local_id"
                class="sortable-item mb-2 p-2 border rounded bg-light position-relative cursor-move transition"
                draggable="true"
                @dragstart="drag_start_section($event, index)"
                @dragover.prevent="drag_over($event)"
                @drop.prevent="drag_drop_section($event, index)"
                @dragend="drag_end($event)"
              >
                <div class="d-flex align-items-center">
                  <div class="drag-handle me-3 text-muted"><i class="fas fa-grip-vertical fs-5"></i></div>
                  <div class="question-number card-header-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 text-center text-nowrap text-xs">
                    {{ index + 1 }}
                  </div>
                  <div class="flex-grow-1">
                    <div class="section-title fw-bold text-xs text-dark">
                      {{ section.title || "Untitled section" }}
                    </div>
                    <div class="text-muted text-xxs">{{ format_section_type(section.section_type) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 p-2 d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow"
              @click="update_section_order"
            >
              Update Order
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</div>
